import React, { useEffect, useState } from "react";
import Cover3dPicker from "./Cover3dPicker";

// The cartridge model a preset key belongs to, from its prefix (gb_red → gb). Game Boy
// and Game Boy Color carts are different 3D models, and one list can hold both (a GBC
// release like Pokémon Gold ships in the GB-shaped cart), so the picker groups by model.
const CART_MODEL_LABELS = { gb: "Game Boy cartridge", gbc: "Game Boy Color cartridge" };
const cartModel = (key) => key.split("_")[0];

function groupByModel(presets) {
  const groups = [];
  for (const preset of presets) {
    const model = cartModel(preset.key);
    const group = groups.find((candidate) => candidate.model === model);
    if (group) group.presets.push(preset);
    else groups.push({ model, presets: [preset] });
  }
  return groups;
}

// "Metadata" on a LaunchBox row: that exact version's launchbox_metadata row
// (GET/PUT/DELETE /admin/launchbox-metadata/:id). Cartridge color is picked only from
// the item's platform presets — the API rejects anything else — and Save sends only
// the fields that changed. Works for an id that was never resolved into
// games_launchbox too (the API reads it live from encyclopedia.db).
function LaunchBoxMetadataModal({ launchboxId, apiFetch, onChanged, onClose }) {
  const [item, setItem] = useState(null);
  const [color, setColor] = useState(null);
  const [disable3d, setDisable3d] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/admin/launchbox-metadata/${launchboxId}`)
      .then((data) => {
        if (cancelled) return;
        setItem(data);
        setColor(data.metadata?.cartridge_color ?? null);
        setDisable3d(data.metadata?.disable_3d ?? null);
      })
      .catch((err) => {
        if (cancelled || err.message === "Forbidden") return;
        setError(err.message === "DATA_NOT_FOUND" ? `No LaunchBox item ${launchboxId}` : err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch, launchboxId]);

  const savedColor = item?.metadata?.cartridge_color ?? null;
  const saved3d = item?.metadata?.disable_3d ?? null;
  const dirty = color !== savedColor || disable3d !== saved3d;
  const colorGroups = groupByModel(item?.cartridge_color_presets || []);
  const unknownColor = savedColor && !(item?.cartridge_color_presets || []).some((preset) => preset.key === savedColor);

  function applied(metadata) {
    setItem((current) => ({ ...current, metadata }));
    setColor(metadata?.cartridge_color ?? null);
    setDisable3d(metadata?.disable_3d ?? null);
    onChanged();
  }

  async function save() {
    const body = {};
    if (color !== savedColor) body.cartridge_color = color;
    if (disable3d !== saved3d) body.disable_3d = disable3d;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const data = await apiFetch(`/admin/launchbox-metadata/${launchboxId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      applied(data.metadata);
      setSaved(true);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete all metadata for "${item.name}" (launchbox_id ${launchboxId})? This includes its own cover images.`)) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiFetch(`/admin/launchbox-metadata/${launchboxId}`, { method: "DELETE" });
      applied(null);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-card glass" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="admin-modal-title">Metadata</h3>
        {!item && !error && <p className="admin-status">Loading…</p>}

        {item && (
          <>
            <div className="admin-source-meta">
              {item.name} · {item.platform_name || `platform_id ${item.platform_id}`} · {item.country_name || "Unknown country"} · launchbox_id {launchboxId}
            </div>
            <div className="admin-boxart-sides">
              {item.cover_url && <img className="admin-boxart admin-boxart--row" src={item.cover_url} alt={`${item.name} front`} title="Front" />}
              {item.label_url && <img className="admin-boxart admin-boxart--row" src={item.label_url} alt={`${item.name} cartridge`} title="Cartridge" />}
            </div>

            <section className="admin-metadata-section">
              <p className="admin-source-meta">Cartridge color</p>
              {item.cartridge_color_presets.length === 0
                ? <p className="admin-source-meta">No cartridge color presets for this platform.</p>
                : (
                  <div className="admin-tgdb-link-form admin-metadata-presets" role="radiogroup" aria-label="Cartridge color">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={color === null}
                      className={`admin-metadata-preset ${color === null ? "admin-metadata-preset--active" : ""}`}
                      onClick={() => { setColor(null); setSaved(false); }}
                      disabled={busy}
                    >
                      <span className="admin-metadata-swatch admin-metadata-swatch--default" />
                      Default
                    </button>
                    {colorGroups.map((group) => (
                      <React.Fragment key={group.model}>
                        {colorGroups.length > 1 && (
                          <span className="admin-metadata-group">{CART_MODEL_LABELS[group.model] || group.model.toUpperCase()}</span>
                        )}
                        {group.presets.map((preset) => (
                          <button
                            type="button"
                            key={preset.key}
                            role="radio"
                            aria-checked={color === preset.key}
                            className={`admin-metadata-preset ${color === preset.key ? "admin-metadata-preset--active" : ""}`}
                            onClick={() => { setColor(preset.key); setSaved(false); }}
                            title={`${preset.key} · ${preset.hex}`}
                            disabled={busy}
                          >
                            <span className="admin-metadata-swatch" style={{ background: preset.hex }} />
                            {preset.name}
                          </button>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              {unknownColor && color === savedColor && (
                <p className="admin-source-meta">
                  Saved value <code>{savedColor}</code> isn't a current preset key — pick one to replace it.
                </p>
              )}
            </section>

            <section className="admin-metadata-section">
              <p className="admin-source-meta">3D box</p>
              <Cover3dPicker value={disable3d} onChange={(key) => { setDisable3d(key); setSaved(false); }} disabled={busy} />
            </section>
          </>
        )}

        {error && <p className="admin-status admin-error">{error}</p>}
        {saved && <p className="admin-status">Saved</p>}

        <div className="admin-row-actions admin-row-actions--gap">
          {item?.metadata && (
            <button type="button" className="admin-signout-btn admin-delete-btn" onClick={remove} disabled={busy}>
              Delete metadata
            </button>
          )}
          <button type="button" className="admin-signout-btn" onClick={onClose}>Close</button>
          <button type="button" className="admin-signout-btn" onClick={save} disabled={busy || !item || !dirty}>
            {busy ? "…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LaunchBoxMetadataModal;
